import type { CSSProperties } from "react";
import { getStateTimelineColors } from "../../lib/workflow-state-colors";
import { formatDuration } from "./StatusPill";
import type { Task } from "../../../lib/types";

export type FocusBlockVariant = "workforce-card" | "agent-profile" | "inline";

export type FocusState = "at-work" | "awaiting-you" | "just-finished" | "available";

export interface FocusBlockProps {
  variant: FocusBlockVariant;
  focus: Task | null;
  focusState: FocusState;
  durationSeconds: number | null;
  equippedWorkflowCount?: number;
  agentName?: string;
}

const STATE_LABELS: Record<FocusState, string> = {
  "at-work": "AT WORK",
  "awaiting-you": "AWAITING YOU",
  "just-finished": "JUST FINISHED",
  available: "AVAILABLE",
};

function getColorBlock(state: FocusState, focus: Task | null) {
  if (state === "at-work" && focus?.workflow_state_label) {
    return getStateTimelineColors(focus.workflow_state_label.toLowerCase().replace(/\s+/g, "-"));
  }
  if (state === "awaiting-you") return getStateTimelineColors("in-review");
  if (state === "just-finished") return getStateTimelineColors("approved");
  return getStateTimelineColors("cancelled");
}

export function deriveFocusState(focus: Task | null, needsYouCount: number): FocusState {
  if (needsYouCount > 0) return "awaiting-you";
  if (!focus) return "available";
  if (focus.status === "done" || focus.status === "dropped") return "just-finished";
  if (focus.actor_kind === "human") return "awaiting-you";
  return "at-work";
}

export function FocusBlock({
  variant,
  focus,
  focusState,
  durationSeconds,
  equippedWorkflowCount = 0,
  agentName: _agentName,
}: FocusBlockProps) {
  void _agentName;
  const colors = getColorBlock(focusState, focus);
  const isAvailable = focusState === "available";
  const isProfile = variant === "agent-profile";

  const durText = formatDuration(durationSeconds);
  const statusSuffix =
    focusState === "just-finished" && durText
      ? `${durText} ago`
      : durText ?? null;

  const container: CSSProperties = isAvailable
    ? {
        background: "var(--color-background-secondary)",
        border: "0.5px solid var(--color-border-tertiary)",
        borderRadius: isProfile ? "var(--border-radius-lg)" : "var(--border-radius-md)",
        padding: isProfile ? "14px 16px" : "10px 12px",
        ...(isProfile ? { marginBottom: 14 } : {}),
      }
    : {
        background: colors.cardBg09,
        border: `0.5px solid ${colors.cardBorder28}`,
        borderRadius: isProfile ? "var(--border-radius-lg)" : "var(--border-radius-md)",
        padding: isProfile ? "14px 16px" : "10px 12px",
        ...(isProfile ? { marginBottom: 14 } : {}),
      };

  const stateLabel = STATE_LABELS[focusState];
  const statusText = statusSuffix ? `${stateLabel} · ${statusSuffix}` : stateLabel;

  const textReadable = isAvailable ? "var(--color-text-tertiary)" : colors.base;

  if (isAvailable) {
    const readyText =
      equippedWorkflowCount > 0
        ? `Knows ${equippedWorkflowCount} workflow${equippedWorkflowCount === 1 ? "" : "s"} · ready when you are`
        : "Ready when you are";
    return (
      <div style={container}>
        <div style={statusPillRow(textReadable)}>
          <span style={statusDot("var(--color-text-tertiary)")} />
          {statusText}
        </div>
        <div
          style={{
            fontSize: isProfile ? 15 : 13,
            fontWeight: 500,
            color: "var(--color-text-secondary)",
            fontStyle: "italic",
            marginTop: 4,
          }}
        >
          {readyText}
        </div>
      </div>
    );
  }

  const focusSubtitle = focus?.workflow_name
    ? `${focus.workflow_name} · ${focus.workflow_state_label ?? "running"}`
    : null;

  return (
    <div style={container}>
      <div style={statusPillRow(textReadable)}>
        <span style={statusDot(colors.base)} />
        {statusText}
      </div>
      {focus && (
        <>
          <div
            style={{
              fontSize: isProfile ? 15 : 13,
              fontWeight: 500,
              color: "var(--color-text-primary)",
              marginTop: 4,
            }}
          >
            {focus.title}
          </div>
          {focusSubtitle && (
            <div
              style={{
                fontSize: 12,
                color: "var(--color-text-secondary)",
                marginTop: 3,
              }}
            >
              {focusSubtitle}
            </div>
          )}
        </>
      )}
      {isProfile && focus && (
        <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
          {focus.workflow_name && (
            <span style={subPill}>workflow: {focus.workflow_name}</span>
          )}
        </div>
      )}
    </div>
  );
}

function statusPillRow(color: string): CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    fontWeight: 500,
    letterSpacing: "0.6px",
    color,
    textTransform: "uppercase",
  };
}

function statusDot(bg: string): CSSProperties {
  return {
    width: 6,
    height: 6,
    borderRadius: "50%",
    background: bg,
    flexShrink: 0,
  };
}

const subPill: CSSProperties = {
  padding: "2px 8px",
  fontSize: 11,
  background: "var(--color-background-secondary)",
  color: "var(--color-text-secondary)",
  borderRadius: 999,
};
